// WP-0.3 (AR-4, LG-1/2): append-only event log. The financial store is a sequence of
// create / correct / reverse / adjust / relink events; current state is derived
// deterministically (projections). No update or delete API exists — history is permanent.
// The engine never reads the clock or generates ids: `at` and `id` come from the caller,
// which is what makes replay deterministic (AR-7) and tests exact.

export const EVENT_KINDS = ['create', 'correct', 'reverse', 'adjust', 'relink'] as const; // LG-2
export type EventKind = (typeof EVENT_KINDS)[number];

/** §18.1 source iconography: ✍ manual · ✦ ai · 🟢 whatsapp · ⇪ import · ⟳ recurring. */
export const EVENT_SOURCES = ['manual', 'ai', 'whatsapp', 'import', 'recurring', 'system'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export interface LedgerEvent<TPayload = unknown> {
  /** Monotonic position assigned by the store at append; the replay order (AR-7). */
  readonly seq: number;
  /** Caller-supplied stable id (UUID in production; fixed strings in tests). */
  readonly id: string;
  readonly kind: EventKind;
  readonly entityType: string;
  readonly entityId: string;
  /** ISO-8601 UTC instant, supplied by the caller — the engine never reads the clock. */
  readonly at: string;
  readonly source: EventSource;
  /** correct/reverse/relink: the prior event being amended. Never mutated (LG-3). */
  readonly amendsEventId?: string;
  /** Audit reason; required for reverse and adjust (TX-1 discipline). */
  readonly reason?: string;
  readonly payload: TPayload;
}

export type NewLedgerEvent<TPayload = unknown> = Omit<LedgerEvent<TPayload>, 'seq'>;

export class EventStoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EventStoreError';
  }
}

/** Kinds that must reference the event they amend. */
const AMENDING_KINDS: ReadonlySet<EventKind> = new Set(['correct', 'reverse', 'relink']);
/** Kinds whose audit trail is meaningless without a stated reason. */
const REASON_REQUIRED: ReadonlySet<EventKind> = new Set(['reverse', 'adjust']);

const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export interface EventStore {
  append(event: NewLedgerEvent): LedgerEvent;
  appendMany(events: readonly NewLedgerEvent[]): LedgerEvent[];
  /** Full log in seq order. Returns a copy — the store cannot be corrupted through it. */
  readAll(): LedgerEvent[];
  /** One entity's audit trail (LG-3), in seq order. */
  readEntity(entityType: string, entityId: string): LedgerEvent[];
  readonly size: number;
}

export class InMemoryEventStore implements EventStore {
  private readonly events: LedgerEvent[] = [];
  private readonly byId = new Map<string, LedgerEvent>();

  append(event: NewLedgerEvent): LedgerEvent {
    this.validate(event);
    const stored: LedgerEvent = Object.freeze({ ...event, seq: this.events.length + 1 });
    this.events.push(stored);
    this.byId.set(stored.id, stored);
    return stored;
  }

  appendMany(events: readonly NewLedgerEvent[]): LedgerEvent[] {
    return events.map((e) => this.append(e));
  }

  readAll(): LedgerEvent[] {
    return [...this.events];
  }

  readEntity(entityType: string, entityId: string): LedgerEvent[] {
    return this.events.filter((e) => e.entityType === entityType && e.entityId === entityId);
  }

  get size(): number {
    return this.events.length;
  }

  private validate(event: NewLedgerEvent): void {
    if (event.id === '') throw new EventStoreError('Event id must not be empty');
    if (this.byId.has(event.id)) {
      throw new EventStoreError(`Duplicate event id: ${event.id} (appends are idempotent-safe)`);
    }
    if (!EVENT_KINDS.includes(event.kind)) {
      throw new EventStoreError(`Unknown event kind: ${event.kind} (LG-2)`);
    }
    if (!EVENT_SOURCES.includes(event.source)) {
      throw new EventStoreError(`Unknown event source: ${event.source}`);
    }
    if (event.entityType === '') throw new EventStoreError('entityType must not be empty');
    if (event.entityId === '') throw new EventStoreError('entityId must not be empty');
    if (!ISO_UTC.test(event.at)) {
      throw new EventStoreError(`"at" must be an ISO-8601 UTC instant; got "${event.at}"`);
    }
    if (AMENDING_KINDS.has(event.kind)) {
      if (event.amendsEventId === undefined) {
        throw new EventStoreError(`${event.kind} must reference the event it amends (LG-3)`);
      }
      const target = this.byId.get(event.amendsEventId);
      if (target === undefined) {
        throw new EventStoreError(`amendsEventId "${event.amendsEventId}" does not exist`);
      }
      if (target.entityType !== event.entityType || target.entityId !== event.entityId) {
        throw new EventStoreError(
          `${event.kind} may only amend an event of the same entity ` +
            `(${event.entityType}/${event.entityId} vs ${target.entityType}/${target.entityId})`,
        );
      }
    } else if (event.amendsEventId !== undefined) {
      throw new EventStoreError(`${event.kind} events must not carry amendsEventId`);
    }
    if (REASON_REQUIRED.has(event.kind) && (event.reason === undefined || event.reason === '')) {
      throw new EventStoreError(`${event.kind} requires a reason (audit discipline)`);
    }
  }
}
